import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Box from '@mui/material/Box';
import { CircularProgress, Grid, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { JBAlert, JBAlertErrorModalProps, JBColors, JBIcon, JBIconsType } from '@bjb-gcrm/gcrm-frontend-components';
import { useFetchPartnerLockStatus } from 'hooks/queries/useFetchPartnerLockStatus';
import { setDlmStatusAndVersion, setTargetDlmStatusAndVersion } from 'reducers/dlmReducer';
import { useFetchPartnerVersions } from 'hooks/queries/useFetchPartnerVersions';
import { SMALL_SCREEN_WIDTH } from 'services/constants';
import {
  selectDlmStatus,
  selectDlmVersion,
  selectTargetDlmStatus,
  selectTargetDlmVersion,
} from 'selectors/dlmSelector';
import { isNotNullOrUndefined } from 'utils/utils';
import { GlobalState } from 'reducers/stateTypes';
import { LockTypes, PartnerLockType } from 'types/PartnerLockTypes';
import { PartnerStatusDto } from 'views/RevisionHistory/Partner/PartnerRevisionDtos';
import { usePostMessageToIFrame } from 'hooks/usePostMessageToIFrame';
import { ClmPortalIncomingPostMessageTypes, ClmPortalOutgoingPostMessageTypes } from 'utils/postMessageUtil';
import { useRuntimeConfig } from 'config/useRuntimeConfig';

const switchStyles = {
  container: {
    position: 'relative',
    height: '32px',
    borderRadius: 20,
    cursor: 'pointer',
    display: 'inline-block',
    marginTop: '8px',
  },
  box: {
    position: 'absolute',
    height: '32px',
    borderRadius: 'inherit',
    transition: 'all 0.3s ease-in-out, z-index 0.1s',
  },
  greyBox: {
    backgroundColor: JBColors.support.greenSmoke[10],
    border: `1px solid ${JBColors.support.greenSmoke[40]}`,
  },
};

export const DlmSwitchStatusTypes = ['ACTIVE', 'DRAFT', 'DRAFT_REVIEW'];
export type DlmSwitchStatus = (typeof DlmSwitchStatusTypes)[number];
export type DlmStatus = DlmSwitchStatus | 'OBSOLETE' | 'DISCARDED';

export const isValidDlmButtonStatus = (dlmStatus?: DlmStatus) =>
  isNotNullOrUndefined(dlmStatus) && DlmSwitchStatusTypes.includes(dlmStatus);
export const isVersionADraft = (dlmStatus: DlmStatus) => (['DRAFT', 'DRAFT_REVIEW'] as DlmStatus[]).includes(dlmStatus);
export const isVersionVisibleInRevisionHistory = (dlmStatus: DlmStatus) =>
  !(['DISCARDED'] as DlmStatus[]).includes(dlmStatus);

export type DlmLightRowType =
  | 'LATEST_CHANGES_WITH_DRAFT'
  | 'LATEST_CHANGES_NO_DRAFT'
  | 'LATEST_CHANGES_NO_VERSIONS'
  | 'NORMAL_VERSION';

export const isLatestChangesDlmLightRow = (dlmLightRowType: DlmLightRowType) =>
  (
    ['LATEST_CHANGES_WITH_DRAFT', 'LATEST_CHANGES_NO_DRAFT', 'LATEST_CHANGES_NO_VERSIONS'] as DlmLightRowType[]
  ).includes(dlmLightRowType);

interface PartnerLockSwitchProps {
  partnerUid: string;
  partnerLocks?: PartnerLockType[];
}

const DraftSwitch = ({ partnerUid, partnerLocks = [] }: PartnerLockSwitchProps) => {
  const { t } = useTranslation();
  const user = useSelector((state: GlobalState) => state.user.userDetails);
  const { envInfo } = useRuntimeConfig();

  const selectedDlmOriginalStatus = useSelector(selectDlmStatus);
  const selectedVersion = useSelector(selectDlmVersion);
  const selectedDlmStatus = isValidDlmButtonStatus(selectedDlmOriginalStatus) ? selectedDlmOriginalStatus : undefined;
  const targetDlmStatus = useSelector(selectTargetDlmStatus);
  const targetVersion = useSelector(selectTargetDlmVersion);

  const { postMessageToIFrameByType } = usePostMessageToIFrame();

  const {
    data: partnerVersions = [],
    isFetching,
    isError,
    isSuccess: isSuccessPartnerVersions,
    error,
    refetch: refetchPartnerVersions,
  } = useFetchPartnerVersions(partnerUid);
  const { refetch: refetchPartnerLock } = useFetchPartnerLockStatus(partnerUid);

  const dispatch = useDispatch();
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const smallScreen = useMediaQuery(`(max-width:${SMALL_SCREEN_WIDTH})`);

  //Order in which to display the buttons
  const statusOrder = ['ACTIVE', 'DRAFT', 'DRAFT_REVIEW'];
  //TODO temp solution until active is always present
  const appendActive = (versions: PartnerStatusDto[]) =>
    versions.some((data: PartnerStatusDto) => data.partnerStatus === 'ACTIVE')
      ? versions
      : versions.concat({
          partnerUid: partnerUid,
          version: 1,
          partnerStatus: 'ACTIVE',
          modifiedBy: user?.username,
          modifiedTimestamp: new Date().toISOString(),
          createdTimestamp: new Date().toISOString(),
          reason: null,
        } as PartnerStatusDto);

  const sortedPartnerRevisions = useMemo(() => {
    const versions = partnerVersions.filter((partnerVersion: PartnerStatusDto) =>
      statusOrder.includes(partnerVersion.partnerStatus),
    );
    return appendActive(versions).sort((a, b) => {
      return statusOrder.indexOf(a.partnerStatus) - statusOrder.indexOf(b.partnerStatus);
    });
  }, [partnerVersions, isSuccessPartnerVersions]);

  const userLock = Array.isArray(partnerLocks)
    ? partnerLocks.find((lockStatus: PartnerLockType) => lockStatus?.lockType === LockTypes.USER)
    : undefined;

  const isPartnerLocked = isNotNullOrUndefined(userLock?.lockContext?.userId);
  const isCurrentUserLocked = userLock?.lockContext?.userId === user?.username;

  const activeStateIndex = sortedPartnerRevisions.findIndex(
    (partnerVersion) => partnerVersion.partnerStatus === selectedDlmStatus,
  );

  const handleMouseEvent = useCallback((index: number | null) => {
    setHoverIndex(index);
  }, []);

  const topSwitchDetails: Record<
    DlmSwitchStatus,
    {
      backgroundColor: string;
      accentColor: string;
      text: string;
      topIconName: string;
    }
  > = useMemo(() => {
    return {
      ACTIVE: {
        backgroundColor: JBColors.support.blue[10],
        accentColor: JBColors.support.blue[120],
        text: t('topbar.versioning.active', 'Viewing'),
        topIconName: 'view',
      },
      DRAFT: {
        backgroundColor: JBColors.support.orange[20],
        accentColor: JBColors.support.orange[120],
        text: isCurrentUserLocked
          ? t('topbar.versioning.draft', 'Editing (Draft)')
          : t('topbar.versioning.lockedDraft', 'Locked (Draft)'),
        topIconName: 'lock',
      },
      DRAFT_REVIEW: {
        backgroundColor: JBColors.support.blue[10],
        accentColor: JBColors.support.blue[110],
        text: t('topbar.versioning.draftInReview', 'Draft in review'),
        topIconName: 'view',
      },
    };
  }, [isPartnerLocked]);

  const bottomSwitchDetails: Record<DlmSwitchStatus, { bottomIconName: string }> = useMemo(() => {
    return {
      ACTIVE: {
        bottomIconName: 'view',
      },
      DRAFT: {
        bottomIconName: 'edit',
      },
      DRAFT_REVIEW: {
        bottomIconName: 'view',
      },
    };
  }, []);

  const { backgroundColor, accentColor, text, topIconName } = isNotNullOrUndefined(selectedDlmStatus)
    ? topSwitchDetails[selectedDlmStatus]
    : topSwitchDetails['ACTIVE'];

  const switchDraftButton = (newDlmStats: DlmStatus | undefined, version?: number | undefined) => {
    if (isNotNullOrUndefined(newDlmStats)) {
      refetchPartnerLock();
      dispatch(setDlmStatusAndVersion({ status: newDlmStats, version }));
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleMessage, false);

    function handleMessage(event: any) {
      const message = event.data.message;
      switch (message) {
        case ClmPortalIncomingPostMessageTypes.CHANGE_TAB: {
          switchDraftButton(targetDlmStatus ?? selectedDlmStatus, targetVersion ?? selectedVersion);
          break;
        }
        // No default
      }
    }

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [targetDlmStatus, targetVersion]);

  if (isError) {
    const errorModalProps: JBAlertErrorModalProps = {
      httpInfoAxiosError: error as JBAlertErrorModalProps['httpInfoAxiosError'],
      technicalInfo: {
        title: 'Error fetching partner versions',
        userErrorMessage: 'We encountered an error while fetching the available partner versions.',
        details: error?.message ?? 'unknown details',
        userInfo: { userId: user?.username ?? 'unknown user' },
        envInfo,
      },
    };
    return (
      <Grid container direction="row" spacing={0} alignItems="center" justifyContent="center">
        <JBAlert color="error" dataTestId="dlm-versions-error" errorModalProps={errorModalProps}>
          {t('topbar.versioning.error', 'Error fetching versions.')}
        </JBAlert>
      </Grid>
    );
  }

  if (sortedPartnerRevisions.length < 2 && !isFetching) {
    return null;
  }

  return (
    <Box alignItems="center" justifyContent="center" mt={-0.75} mr={smallScreen ? 5 : 19}>
      {isFetching || !selectedDlmStatus ? (
        <Box sx={{ marginTop: '12px' }}>
          <CircularProgress size={16} id="dlm-loading-progressbar" data-testid="dlm-loading-progressbar" />
        </Box>
      ) : (
        <Box display="flex">
          <Box
            sx={{
              display: 'flex',
              justifyContent: smallScreen ? 'flex-end' : 'center',
              mr: smallScreen ? -16 : 0,
            }}
          >
            <Box
              sx={{
                ...switchStyles.container,
              }}
              onClick={undefined}
            >
              <Tooltip
                title={<Trans t={t} i18nKey={`dlm.switch.tooltip.${selectedDlmStatus}`} />}
                arrow
                placement={selectedDlmStatus === 'ACTIVE' ? 'bottom-end' : 'bottom-start'}
              >
                <Box
                  display="flex"
                  id={`dlm-active-switch-${selectedDlmStatus.toLowerCase()}`}
                  data-testid={`dlm-active-switch-${selectedDlmStatus.toLowerCase()}`}
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    ...switchStyles.box,
                    backgroundColor,
                    width: smallScreen ? 32 : 150,
                    border: `1px solid ${accentColor}`,
                    zIndex: sortedPartnerRevisions.length + 1,
                    transform: `translateX(${(activeStateIndex - 1) * 50}px)`,
                  }}
                >
                  <JBIcon iconName={topIconName as JBIconsType} size="lg" fill={accentColor} />
                  {!smallScreen && (
                    <Typography
                      variant="h6"
                      color={accentColor}
                      sx={{ marginLeft: '8px' }}
                      id={`dlm-switch-status-text-${selectedDlmStatus.toLowerCase()}`}
                      data-testid={`dlm-switch-status-text-${selectedDlmStatus.toLowerCase()}`}
                    >
                      {text}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
              {sortedPartnerRevisions.map((partnerVersion: PartnerStatusDto, index: number) => {
                const isHovered = hoverIndex === index;
                const isStatusSelected = partnerVersion.partnerStatus === selectedDlmStatus;
                const {
                  backgroundColor: hoveredBackgroundColor,
                  accentColor: hoveredAccentColor,
                  text: hoveredText,
                  topIconName: hoveredIcon,
                } = topSwitchDetails[partnerVersion.partnerStatus as DlmSwitchStatus];
                const { bottomIconName } = bottomSwitchDetails[partnerVersion.partnerStatus as DlmSwitchStatus];
                return (
                  <Box
                    id={`partner-lock-bottom-box-${partnerVersion.partnerStatus.toLowerCase()}`}
                    data-testid={`partner-lock-bottom-box-${partnerVersion.partnerStatus.toLowerCase()}`}
                    key={index}
                    onMouseEnter={() => handleMouseEvent(index)}
                    onMouseLeave={() => handleMouseEvent(null)}
                    onClick={async () => {
                      const switchToButton = partnerVersion.partnerStatus;

                      // Only perform logic if transitioning from "ACTIVE" to "DRAFT"
                      if (switchToButton === 'DRAFT') {
                        const { data: latestPartnerVersions } = await refetchPartnerVersions();

                        if (!latestPartnerVersions) return;

                        // Check if a draft version exists
                        const draftVersion = latestPartnerVersions.find((v) => isVersionADraft(v.partnerStatus));

                        // Check if a draft review version exists
                        const draftReviewVersion = latestPartnerVersions.find(
                          (v) => v.partnerStatus === 'DRAFT_REVIEW',
                        );

                        // Check if an active version exists
                        const activeVersion = latestPartnerVersions.find((v) => v.partnerStatus === 'ACTIVE');

                        if (draftVersion) {
                          // Switch to the draft version if it exists
                          switchDraftButton(draftVersion.partnerStatus, draftVersion.version);
                          dispatch(
                            setTargetDlmStatusAndVersion({
                              status: draftVersion.partnerStatus,
                              version: draftVersion.version,
                            }),
                          );
                        } else if (draftReviewVersion) {
                          // Switch to the draft review version if it exists
                          switchDraftButton(draftReviewVersion.partnerStatus, draftReviewVersion.version);
                          dispatch(
                            setTargetDlmStatusAndVersion({
                              status: draftReviewVersion.partnerStatus,
                              version: draftReviewVersion.version,
                            }),
                          );
                        } else {
                          // If not, fallback to the active version
                          await refetchPartnerLock();
                          dispatch(
                            setTargetDlmStatusAndVersion({
                              status: activeVersion!.partnerStatus,
                              version: activeVersion!.version,
                            }),
                          );
                        }
                        return;
                      }

                      const doWeNeedUserConfirmationToLeaveDraftMode =
                        isNotNullOrUndefined(selectedDlmStatus) && isVersionADraft(selectedDlmStatus);

                      if (doWeNeedUserConfirmationToLeaveDraftMode) {
                        dispatch(
                          setTargetDlmStatusAndVersion({
                            status: switchToButton,
                            version: partnerVersion.version,
                          }),
                        );
                        postMessageToIFrameByType(ClmPortalOutgoingPostMessageTypes.CHANGE_TAB_SAVE_CHECK);
                      } else {
                        switchDraftButton(partnerVersion.partnerStatus, partnerVersion.version);
                        dispatch(
                          setTargetDlmStatusAndVersion({
                            status: partnerVersion.partnerStatus,
                            version: partnerVersion.version,
                          }),
                        );
                      }
                    }}
                    sx={{
                      pointerEvents: isFetching ? 'none' : 'auto',
                      ...switchStyles.box,
                      ...switchStyles.greyBox,
                      width: smallScreen ? 32 : 150,
                      left: `calc(${index * 50}px - 50px)`,
                      padding: smallScreen ? 0 : '0 20px',
                      '&:active': {
                        transform: 'scale(1)',
                      },
                      zIndex:
                        index % 2 === 0 ? sortedPartnerRevisions.length - index - 1 : sortedPartnerRevisions.length,
                      ...((isHovered || isStatusSelected) && {
                        backgroundColor: hoveredBackgroundColor,
                        border: `1px solid ${hoveredAccentColor}`,
                        transition: 'all 0.3s ease-in-out, padding 0s',
                        padding: 0,
                        ...(!isStatusSelected && {
                          zIndex: sortedPartnerRevisions.length + 1,
                          transform: 'scale(1.08)',
                        }),
                      }),
                    }}
                    display="flex"
                    justifyContent={hoverIndex === index || smallScreen ? 'center' : 'space-between'}
                    alignItems="center"
                  >
                    {isHovered ? (
                      <>
                        <JBIcon iconName={hoveredIcon as JBIconsType} size="lg" fill={hoveredAccentColor} />
                        {!smallScreen && (
                          <Typography
                            variant="h6"
                            color={hoveredAccentColor}
                            sx={{ marginLeft: '8px', transition: switchStyles.box.transition }}
                            id={`dlm-switch-status-text-${selectedDlmStatus.toLowerCase()}`}
                            data-testid={`dlm-switch-status-text-${selectedDlmStatus.toLowerCase()}`}
                          >
                            {hoveredText}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <>
                        {!smallScreen && !isStatusSelected && (
                          <JBIcon
                            iconName={bottomIconName as JBIconsType}
                            size="lg"
                            fill={JBColors.support.greenSmoke[50]}
                          />
                        )}
                        {!isStatusSelected && (
                          <JBIcon
                            iconName={bottomIconName as JBIconsType}
                            size="lg"
                            fill={JBColors.support.greenSmoke[50]}
                          />
                        )}
                      </>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DraftSwitch;
